import React, { useEffect, useState } from 'react';
import { Box } from '@grubhubprod/gh-cookbook-react';
import styled from 'styled-components';
import { getWidthPercent } from 'src/components/common/components/ProgressBar/progressBar.utils';

interface ProgressBarFillProps {
  disableTransition: boolean;
  transitionDuration: number;
}

export interface ProgressBarProps extends Partial<ProgressBarFillProps> {
  progress: number;
  height?: number;
  borderRadius?: number;
  backgroundColor?: string;
  fillColor?: string;
  animationDelay?: number;
  ariaLabel?: string;
}

export const PROGRESS_BAR_TRANSITION_DURATION = 1000;

export const ProgressBarFill = styled(Box)<ProgressBarFillProps>`
  transition: ${({
    disableTransition,
    transitionDuration,
  }: ProgressBarFillProps): string =>
    disableTransition
      ? 'none'
      : `width ${transitionDuration}ms cubic-bezier(0.4, 0, 1, 1)`};
`;

export const ProgressBar = ({
  progress,
  height = 6,
  borderRadius = 6,
  backgroundColor = 'tintPrimary',
  fillColor = 'colorSecondary',
  disableTransition = false,
  animationDelay = 0,
  transitionDuration = PROGRESS_BAR_TRANSITION_DURATION,
  ariaLabel = 'Progress',
}: ProgressBarProps): JSX.Element => {
  const targetWidth = getWidthPercent(progress);
  const [currentWidth, setCurrentWidth] = useState(targetWidth);

  useEffect(() => {
    if (animationDelay > 0) {
      const timeout = setTimeout(() => {
        setCurrentWidth(targetWidth);
      }, animationDelay);

      return () => clearTimeout(timeout);
    }

    setCurrentWidth(targetWidth);
    return undefined;
  }, [targetWidth, animationDelay]);

  return (
    <Box
      role="progressbar"
      aria-valuenow={currentWidth}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      height={`${height}px`}
      width="100%"
      borderRadius={`${borderRadius}px`}
      position="relative"
      backgroundColor={backgroundColor}
      data-testid="progress-bar"
    >
      <ProgressBarFill
        position="absolute"
        top={0}
        left={0}
        height={`${height}px`}
        width={`${currentWidth}%`}
        borderRadius={`${borderRadius}px`}
        backgroundColor={fillColor}
        disableTransition={disableTransition}
        transitionDuration={transitionDuration}
        data-testid="progress-bar-fill"
      />
    </Box>
  );
};
